/**
 * Enhanced API client with production-ready error handling,
 * retry logic, and request/response interceptors
 */
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add correlation ID for request tracking
    const correlationId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    config.headers['X-Correlation-ID'] = correlationId;
    
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 ${config.method.toUpperCase()} ${config.url}`, config.data);
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      const responseTime = response.headers['x-response-time'];
      console.log(`📥 ${response.config.method.toUpperCase()} ${response.config.url} (${responseTime || 'N/A'})`, response.data);
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle network errors
    if (!error.response) {
      const errorMsg = error.code === 'ECONNABORTED' 
        ? 'Request timeout. Please try again.'
        : 'Network error. Please check your connection.';
      
      toast.error(errorMsg);
      return Promise.reject({
        type: 'network_error',
        message: errorMsg,
        originalError: error,
      });
    }
    
    const { status, data } = error.response;
    const correlationId = error.response.headers['x-correlation-id'];
    
    // Log error with correlation ID
    console.error(`❌ API Error [${status}]`, {
      url: originalRequest.url,
      correlationId,
      error: data,
    });
    
    // Handle specific error status codes
    switch (status) {
      case 401:
        // Unauthorized - clear token and redirect to login
        if (!originalRequest._retry && !originalRequest.url.includes('/auth/login')) {
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
        }
        break;
      
      case 403:
        toast.error('Access forbidden. You do not have permission.');
        break;
      
      case 404:
        toast.error('Resource not found.');
        break;
      
      case 413:
        toast.error('Request too large. Please reduce the data size.');
        break;
      
      case 422:
        // Validation error - show detailed validation messages
        if (data?.error?.detail && Array.isArray(data.error.detail)) {
          const validationErrors = data.error.detail
            .map(err => `${err.field}: ${err.message}`)
            .join('\n');
          toast.error('Validation Error', {
            description: validationErrors,
          });
        } else {
          toast.error(data?.error?.message || 'Validation error. Please check your input.');
        }
        break;
      
      case 429:
        toast.error('Too many requests. Please slow down and try again later.');
        break;
      
      case 500:
      case 502:
      case 503:
      case 504:
        toast.error('Server error. Please try again later.', {
          description: correlationId ? `Reference ID: ${correlationId}` : undefined,
        });
        break;
      
      default:
        // Generic error message
        const errorMessage = data?.error?.message || data?.message || 'An error occurred. Please try again.';
        toast.error(errorMessage, {
          description: correlationId ? `Reference ID: ${correlationId}` : undefined,
        });
    }
    
    // Return standardized error object
    return Promise.reject({
      status,
      message: data?.error?.message || data?.message || 'Request failed',
      detail: data?.error?.detail,
      correlationId,
      originalError: error,
    });
  }
);

/**
 * API helper functions with better error handling
 */
export const apiHelpers = {
  /**
   * Safe API call wrapper with loading state and error handling
   */
  async safeCall(apiFunction, options = {}) {
    const {
      loadingMessage = 'Loading...',
      successMessage = null,
      onSuccess = null,
      onError = null,
      showLoading = false,
    } = options;
    
    let toastId;
    if (showLoading) {
      toastId = toast.loading(loadingMessage);
    }
    
    try {
      const result = await apiFunction();
      
      if (toastId) {
        toast.dismiss(toastId);
      }
      
      if (successMessage) {
        toast.success(successMessage);
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return { success: true, data: result };
    } catch (error) {
      if (toastId) {
        toast.dismiss(toastId);
      }
      
      if (onError) {
        onError(error);
      }
      
      return { success: false, error };
    }
  },
  
  /**
   * Retry API call with exponential backoff
   */
  async retry(apiFunction, options = {}) {
    const {
      maxRetries = 3,
      initialDelay = 1000,
      maxDelay = 10000,
      backoffMultiplier = 2,
    } = options;
    
    let lastError;
    let delay = initialDelay;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await apiFunction();
      } catch (error) {
        lastError = error;
        
        // Don't retry on client errors (4xx) except 429 (rate limit)
        if (error.status >= 400 && error.status < 500 && error.status !== 429) {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries - 1) {
          throw error;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * backoffMultiplier, maxDelay);
      }
    }
    
    throw lastError;
  },
};

export default api;
